package ui.app.assign.detail

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import api.AppType
import apps.App
import compose.assets.Svg
import compose.assets.svg.Package_down
import compose.assets.svg.Package_up
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.layer_bottom_detail__action_move_from_archive_to_active
import ssoft.frontend.generated.resources.layer_bottom_detail__action_move_to_archive
import ssoft.frontend.generated.resources.layer_top_detail__search_device
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.SearchWrapper
import tools.rememberString
import ui.app.assign.detail.definition.*
import ui.app.assign.detail.kindergarten.preview.AppKindergartenPreviewUI
import ui.app.assign.detail.wheel.preview.AppPreviewWheelUI
import ui.app.assign.typeName
import ui.widget.*

data class AppDetailArgs(var companyId: Long = -1, var appId: Long = -1)

@ComposableNavNode(AppDetailArgs::class)
@Composable
fun AppDetailUI() {
    with(AppDetailScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun AppDetailScope.Content(nnvl: AppDetailScope.Wrapper) {
    when (nnvl.data.data) {
        is App.Wheel -> AppPreviewWheelUI()
        is App.Kindergarten -> AppKindergartenPreviewUI()
        is App.Balloon,
        is App.Camera,
        is App.Gallery,
        is App.Image,
        is App.Maze,
        is App.None,
        is App.Pdf,
        is App.Quiz,
        is App.Rss,
        is App.UsbPlayer,
        is App.Video,
        is App.VideoAlphaLoop,
        is App.WwwEmbedded,
        is App.WwwLink,
        is App.AppGroup -> ContentInternal(nnvl)
    }
}

@Composable
fun AppDetailScope.ContentInternal(nnvl: AppDetailScope.Wrapper) {
    val deleteDialogState = DialogState.remember()

    AppDeleteDialog(deleteDialogState, nnvl.type)

    val saveState = nnvl.canBeSaved()
    val toolbarActions = remember {
        buildList {
            if (nnvl.canEdit || nnvl.canDelete) {
                add(ToolbarAction.delete {
                    deleteDialogState.show()
                })
            }
            if (nnvl.canEdit) {
                if (nnvl.data.archived) {
                    add(ToolbarAction(Svg.Package_up, tooltip = Res.string.layer_bottom_detail__action_move_from_archive_to_active) {
                        action(AppDetailVM.Action.Archive(false))
                    })
                } else {
                    add(ToolbarAction(Svg.Package_down, tooltip = Res.string.layer_bottom_detail__action_move_to_archive) {
                        action(AppDetailVM.Action.Archive(true))
                    })
                }

            }
            add(ToolbarAction.save(saveState) {
                action(AppDetailVM.Action.Save)
            })
        }
    }

    val title = if (nnvl.type !is AppType.RSS) nnvl.type.typeName().rememberString()
    else nnvl.data.variant.name

    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = title,
            titleSuffix = nnvl.data.id.toString(),
            actions = toolbarActions
        )
        if (display.isMobile || display.resolution.width < 1000) {
            ContentMobile(nnvl)
        } else {
            ContentDesktop(nnvl)
        }
    }
}

@Composable
fun AppDetailScope.ContentMobile(nnvl: AppDetailScope.Wrapper) {
    Column(Modifier.fillMaxSize()) {
        Crossfade(
            targetState = vm.tabSelected,
            modifier = Modifier.weight(1F)
        ) {
            when (it) {
                AppDetailVM.TabTag.CONFIG_PRIMARY -> PanelPrimary(nnvl, Modifier.fillMaxSize())
                AppDetailVM.TabTag.CONFIG_SECONDARY -> PanelSecondary(nnvl, Modifier.fillMaxSize())
                AppDetailVM.TabTag.DEVICES -> PanelDevices(nnvl, Modifier.fillMaxSize())
            }
        }
        NavigationBar(Modifier.fillMaxWidth()) {
            vm.tabs.forEachIndexed { index, tabDefinition ->
                NavigationBarItem(
                    selected = tabDefinition.tag == vm.tabSelected,
                    onClick = { with(tabDefinition) { onClick(index) } },
                    label = { Text(tabDefinition.label.rememberString()) },
                    icon = { Icon(tabDefinition.icon, null) })
            }
        }
    }
}

@Composable
fun AppDetailScope.ContentDesktop(nnvl: AppDetailScope.Wrapper) {
    Row(Modifier.fillMaxSize()) {
        Box(Modifier.weight(1F)) {
            PanelPrimary(nnvl, Modifier.align(Alignment.TopCenter))
        }
        VerticalDivider(modifier = Modifier.padding(vertical = 16.dp))
        if (nnvl.app.containsSecondaryPanel) {
            Box(Modifier.weight(1F)) {
                PanelSecondary(nnvl, Modifier.align(Alignment.TopCenter))
            }
            VerticalDivider(modifier = Modifier.padding(vertical = 16.dp))
        }
        Box(Modifier.weight(1.5F)) {
            PanelDevices(nnvl, Modifier.align(Alignment.TopCenter))
        }
    }
}

@Composable
fun AppDetailScope.PanelPrimary(nnvl: AppDetailScope.Wrapper, modifier: Modifier) {
    val scrollState = rememberScrollState()
    Column(modifier.fillMaxHeight().verticalScroll(scrollState).padding(16.dp)) {
        when (val config = nnvl.app) {
            is AppDetailScope.WrapperDefinition.OfImage -> AppDefOfImage(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfCamera -> AppDefOfCamera(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfMaze -> AppDefOfMaze(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfVideo -> AppDefOfVideo(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfPdf -> AppDefOfPdf(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfWwwEmbedded -> AppDefOfWwwEmbedded(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfWwwLink -> AppDefOfWwwLink(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfRss -> AppDefOfRss(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfAppGroup -> AppDefOfAppsGroupConfig(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfBalloon -> AppDefOfBalloon(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfQuiz -> AppDefOfQuiz(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfGallery -> AppDefOfGallery(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfVideoAlphaLoop -> AppDefOfVideoAlphaLoop(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfUsbPlayer -> AppDefOfUsbPlayer(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfNone -> Unit
        }
    }
}

@Composable
fun AppDetailScope.PanelSecondary(nnvl: AppDetailScope.Wrapper, modifier: Modifier) {
    Box(modifier.fillMaxHeight()) {
        when (val config = nnvl.app) {
            is AppDetailScope.WrapperDefinition.OfAppGroup -> AppDefOfAppsGroupApps(config)
            is AppDetailScope.WrapperDefinition.OfQuiz -> AppDefOfQuizQuestions(nnvl, config)
            is AppDetailScope.WrapperDefinition.OfGallery -> AppDefOfGalleryMedia(config)
            is AppDetailScope.WrapperDefinition.OfImage,
            is AppDetailScope.WrapperDefinition.OfVideo,
            is AppDetailScope.WrapperDefinition.OfVideoAlphaLoop,
            is AppDetailScope.WrapperDefinition.OfPdf,
            is AppDetailScope.WrapperDefinition.OfWwwEmbedded,
            is AppDetailScope.WrapperDefinition.OfWwwLink,
            is AppDetailScope.WrapperDefinition.OfRss,
            is AppDetailScope.WrapperDefinition.OfBalloon,
            is AppDetailScope.WrapperDefinition.OfMaze,
            is AppDetailScope.WrapperDefinition.OfUsbPlayer,
            is AppDetailScope.WrapperDefinition.OfCamera,
            is AppDetailScope.WrapperDefinition.OfNone -> Unit
        }
    }
}

@Composable
fun AppDetailScope.PanelDevices(nnvl: AppDetailScope.Wrapper, modifier: Modifier) {
    val searchFiled = remember {
        mutableStateOf(TextFieldValue(""))
    }
    Column(modifier.fillMaxHeight().padding(16.dp)) {
        SearchUI(searchFiled, label = Res.string.layer_top_detail__search_device)
        Space(16.dp)
        Box(Modifier.fillMaxSize()) {
            LazyVerticalStaggeredGrid(
                modifier = Modifier.fillMaxWidth(),
                columns = StaggeredGridCells.FixedSize(384.dp),
                verticalItemSpacing = 24.dp,
                horizontalArrangement = Arrangement.spacedBy(24.dp, Alignment.Start),
            ) {
                itemsIndexed(
                    SearchWrapper.use(
                        fields = { it.data.devices.map { it.name } + it.data.name },
                        items = nnvl.locations.state.filter { it.devices.state.isNotEmpty() },
                        query = searchFiled,
                        sort = compareBy {
                            it.data.name.lowercase()
                        }
                    )) { _, item ->
                    ListingItem(nnvl.locations.canEdit, item)
                }
                item {
                    Space(64.dp)
                }
            }
        }
    }
}

@Composable
private fun AppDetailScope.ListingItem(
    canEdit: Boolean,
    item: AppDetailScope.WrapperLocations.Item
) {
    Column {
        Text(
            text = item.data.name,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            style = MaterialTheme.typography.titleSmall,
            modifier = Modifier.padding(4.dp)
        )
        Card {
            item.devices.state
                .sortedBy { it.data.name.lowercase() }
                .forEachIndexed { index, item ->
                    if (index != 0) HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 8.dp),
                        color = MaterialTheme.colorScheme.surface
                    )
                    Item(item, canEdit)
                }
        }
    }
}


@Composable
private fun AppDetailScope.Item(
    entry: AppDetailScope.WrapperLocationDevice.Item,
    canEdit: Boolean
) {

    Row(
        Modifier
            .clickable(canEdit) {
                entry.enabled.set(!entry.enabled.state)
            }
            .padding(vertical = 4.dp)
            .fillMaxWidth()) {
        Space(8.dp)
        IconicSwitch(
            enabled = canEdit,
            checked = entry.enabled.state,
            onCheckedChange = { newState ->
                entry.enabled.set(newState)
            },
            modifier = Modifier.padding(vertical = 4.dp)
                .align(Alignment.CenterVertically)
        )
        Space(8.dp)
        Text(
            text = entry.data.name,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.align(Alignment.CenterVertically)
                .padding(end = 4.dp)
                .weight(1F)
        )
    }
}

@Composable
private fun AppDetailScope.Wrapper.canBeSaved(): State<Boolean> {
    val state = remember {
        mutableStateOf(false)
    }
    LaunchedEffect(canEdit, change, app.changed, locations.state.any { it.change }) {
        state.value = canEdit && (change || app.changed || locations.state.any { it.change })
    }

    return state
}
